import React from 'react';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Calendar,
  CreditCard,
  Settings,
  BarChart3,
  Megaphone,
  Newspaper,
  FileText,
  Target,
  Lock,
  PieChart,
  BookOpen,
  LayoutList,
  Network,
  ShieldCheck,
  Vote,
  ShieldAlert,
  PlusCircle,
  Zap,
  LifeBuoy,
  Boxes,
  Mail,
} from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { useModule } from './useModules';
import { hasTierAccess } from '../constants';

/**
 * NavItem / NavGroup
 *
 * Single source of truth for the app's primary navigation. Both the desktop
 * Sidebar and the mobile BottomNav render from this, so a destination can no
 * longer exist on one and be missing on the other (or carry a different label).
 *
 * Items are pre-filtered: an item only appears when the module is enabled AND
 * the viewer holds the required capability. `locked` marks items the viewer can
 * see but cannot open at their plan tier.
 */
export interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: number;
  locked?: boolean;
}

export interface NavGroup {
  id: 'main' | 'communications' | 'org' | 'admin';
  label: string;
  accent: 'blue' | 'yellow' | 'black';
  items: NavItem[];
}

export interface UseNavItemsResult {
  groups: NavGroup[];
}

export const useNavItems = (): UseNavItemsResult => {
  const { node, isGlobal, isUniversal, getTerm } = useTenant();
  const { user, memberships = [] } = useCurrentUser();
  const { can, isAdmin, isSystemAdmin, isSuperAdmin } = usePermissions();

  const hasPulse = useModule('PULSE');
  const hasNewsletter = useModule('NEWSLETTER');
  const hasChat = useModule('CHAT');
  const hasCalendar = useModule('CALENDAR');
  const hasLedger = useModule('LEDGER');
  const hasRequirements = useModule('REQUIREMENTS');
  const hasGoals = useModule('GOALS');
  const hasVault = useModule('VAULT');
  const hasPolls = useModule('POLLS');
  const hasDirectory = useModule('DIRECTORY');
  const hasDiscussions = useModule('DISCUSSIONS');
  const hasElections = useModule('ELECTIONS');
  const hasSupport = useModule('SUPPORT');

  // Platform-staff check. Previously duplicated (and drifting) between the
  // Sidebar and BottomNav; now it lives in one place.
  const isPlatformAdmin = Boolean(
    isSystemAdmin ||
      isSuperAdmin ||
      user?.isSuperAdmin ||
      user?.superAdmin ||
      user?.isSuperUser ||
      user?.superUser ||
      user?.publicMetadata?.isSuperAdmin ||
      user?.publicMetadata?.isSuperUser ||
      user?.role === 'SUPER_ADMIN' ||
      user?.role === 'SUPER_USER' ||
      user?.role === 'SUPERUSER'
  );

  const currentMembership = memberships.find(m => m.nodeSlug === node?.slug);
  const unreadPulse = isUniversal
    ? memberships.reduce((acc, m) => acc + (m.unreadCount || 0), 0)
    : currentMembership?.unreadCount || 0;

  // Tier locks come from the shared policy (constants.ts) so the nav, the route
  // guard and the page always agree. Only meaningful inside a specific org.
  const tierOpts = { isSystemAdmin, isSuperAdmin };
  const isChatLocked = Boolean(node) && !isGlobal && !hasTierAccess('chat', node?.tier, tierOpts);
  const isSubOrgsLocked = !hasTierAccess('subOrgs', node?.tier, tierOpts);

  const groups: NavGroup[] = [];

  // --- Main ---------------------------------------------------------------
  const main: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/invitations', icon: Mail, label: 'Invitations' },
    { to: '/toolbox', icon: Boxes, label: 'Toolbox' },
  ];
  if (isAdmin || isUniversal || isSystemAdmin || isSuperAdmin) {
    main.push({ to: '/create-org', icon: PlusCircle, label: 'Create Org' });
  }
  if (isPlatformAdmin) {
    main.push({ to: '/system-admin', icon: ShieldAlert, label: 'System Admin' });
  }
  if (isUniversal) {
    main.push({ to: '/powerups', icon: Zap, label: 'Powerups' });
    if (hasSupport) main.push({ to: '/support', icon: LifeBuoy, label: 'Help Desk' });
  }
  if (main.length) groups.push({ id: 'main', label: 'Main', accent: 'blue', items: main });

  // --- Communications -----------------------------------------------------
  // Hidden on the platform root (no org context) unless the user is viewing all orgs.
  if (!isGlobal || isUniversal) {
    const comms: NavItem[] = [];
    if (hasPulse) {
      comms.push({
        to: '/pulse',
        icon: Megaphone,
        label: isUniversal ? 'All Activity' : getTerm('pulse'),
        badge: unreadPulse,
      });
    }
    if (hasDiscussions && can('view_discussions')) {
      comms.push({ to: '/discussions', icon: LayoutList, label: getTerm('discussions') });
    }
    if (hasNewsletter && can('view_newsletter')) {
      comms.push({ to: '/newsletter', icon: Newspaper, label: 'Newsletters' });
    }
    if (hasChat && can('view_chat')) {
      comms.push({ to: '/chat', icon: MessageSquare, label: 'Chat', locked: isChatLocked });
    }
    if (hasCalendar && can('view_calendar')) {
      comms.push({ to: '/calendar', icon: Calendar, label: 'Calendar' });
    }
    if (comms.length) {
      groups.push({ id: 'communications', label: 'Communications', accent: 'yellow', items: comms });
    }
  }

  // --- Organization -------------------------------------------------------
  if (!isUniversal && !isGlobal) {
    const org: NavItem[] = [];
    if (hasLedger && (can('view_ledger_admin') || can('view_personal_finances'))) {
      org.push({
        to: '/ledger',
        icon: CreditCard,
        label: can('view_ledger_admin') ? getTerm('ledger') : 'My Payments',
      });
    }
    if (can('manage_members')) {
      org.push({ to: '/members', icon: Users, label: `${getTerm('member')}s` });
    }
    if (can('view_leadership')) {
      org.push({ to: '/leadership', icon: ShieldCheck, label: 'Leadership' });
    }
    if (hasElections && can('view_elections')) {
      org.push({ to: '/elections', icon: Vote, label: 'Elections' });
    }
    if (hasSupport && can('view_support')) {
      org.push({ to: '/support', icon: LifeBuoy, label: getTerm('support') || 'Support' });
    }
    if (hasRequirements && can('view_requirements')) {
      org.push({ to: '/requirements', icon: FileText, label: getTerm('requirements') });
    }
    if (hasGoals && can('view_goals')) {
      org.push({ to: '/goals', icon: Target, label: getTerm('goals') });
    }
    if (hasVault && can('manage_vault')) {
      org.push({ to: '/vault', icon: Lock, label: 'Vault' });
    }
    if (hasPolls && can('view_polls')) {
      org.push({ to: '/polls', icon: PieChart, label: 'Polls' });
    }
    if (hasDirectory && can('view_directory')) {
      org.push({ to: '/directory', icon: BookOpen, label: 'Directory' });
    }
    if (org.length) {
      groups.push({ id: 'org', label: node?.name || 'Organization', accent: 'black', items: org });
    }
  }

  // --- Admin --------------------------------------------------------------
  const showAdmin =
    (can('view_analytics') || can('edit_settings') || isAdmin || isSystemAdmin || isSuperAdmin) &&
    !isUniversal &&
    !isGlobal;

  if (showAdmin) {
    const admin: NavItem[] = [];
    if (can('view_suborgs') && (isAdmin || isSystemAdmin || isSuperAdmin)) {
      admin.push({
        to: '/sub-orgs',
        icon: Network,
        label: `${getTerm('childNode')}s`,
        locked: isSubOrgsLocked,
      });
    }
    if (isAdmin || isSystemAdmin || isSuperAdmin) {
      admin.push({ to: '/powerups', icon: Zap, label: 'Powerups' });
    }
    if (can('view_analytics')) {
      admin.push({ to: '/analytics', icon: BarChart3, label: 'Analytics' });
    }
    if (can('edit_settings')) {
      admin.push({ to: '/settings', icon: Settings, label: 'Settings' });
    }
    if (admin.length) {
      groups.push({ id: 'admin', label: 'Admin', accent: 'blue', items: admin });
    }
  }

  return { groups };
};
